<template>
  <v-container fluid>
    <v-layout row>
      <v-flex>
        <v-card>
          <v-card-title primary-title class="primary white--text headline">
            Topic Models
            <v-spacer></v-spacer>
            <v-btn medium depressed flat icon color="white" to="topic-models/add" v-if="$auth.user().AuthScope.includes('topic:manage')">
              <v-icon medium>add_circle_outline</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider></v-divider>
          <v-card-text class="pa-0">
            <v-data-table
            :headers="headers"
            :items="items"
            hide-actions
            :rows-per-page-items="[25,50,100,{'text':'$vuetify.dataIterator.rowsPerPageAll','value':-1}]"
            :loading="topicModelListLoading">
              <v-progress-linear slot="progress" color="primary" indeterminate></v-progress-linear>
              <template slot="items" slot-scope="props">
                <td>{{ props.item.id }}</td>
                <td>{{ props.item.name }}</td>
                <td>{{ props.item.language.name }}</td>
                <td class="justify-end layout pl-0 pr-2 py-2">
                          <v-tooltip bottom color="grey">
                            <v-icon
                              class="mr-2"
                              color="grey"
                              slot="activator"
                              @click="$router.push({name: 'topicModelExport', params: {id: props.item.id, name: props.item.name}})"
                            >
                              cloud_download
                            </v-icon>
                            <span>Export Topic Model</span>
                          </v-tooltip>
                          <v-tooltip bottom color="grey" v-if="$auth.user().AuthScope.includes('topic:manage')">
                            <v-icon
                              class="mr-2"
                              color="grey"
                              slot="activator"
                              :disabled="packageLoadingId === props.item.id"
                              @click="downloadPackage(props.item)"
                            >
                              archive
                            </v-icon>
                            <span>Download Model Package (JSON + README)</span>
                          </v-tooltip>
                          <v-tooltip bottom color="success">
                            <v-icon
                              class="mr-2"
                              color="success"
                              slot="activator"
                              @click="$router.push(`/topic-models/${props.item.id}/statistics`)"
                            >
                              insert_chart
                            </v-icon>
                            <span>Show Statistics</span>
                          </v-tooltip>
                          <v-tooltip bottom color="secondary">
                            <v-icon
                              @click="$router.push({name: 'topicModelTry', params: {id: props.item.id, name: props.item.name}})"
                              class="mr-2"
                              color="secondary"
                              slot="activator">
                              directions_run
                            </v-icon>
                            Try Model
                          </v-tooltip>
                          <v-tooltip bottom color="accent" v-if="$auth.user().AuthScope.includes('topic:manage')">
                            <v-icon
                              class="mr-2"
                              color="accent"
                              slot="activator"
                              @click="$router.push(`/topic-models/${props.item.id}/edit`)"
                            >
                              edit
                            </v-icon>
                            <span>Edit Item</span>
                          </v-tooltip>
                          <v-tooltip bottom color="error" v-if="$auth.user().AuthScope.includes('topic:manage')">
                            <v-icon
                              color="error"
                              slot="activator"
                              @click="dialog = true; itemToDelete = props.item;"
                            >
                              delete
                            </v-icon>
                            <span>Delete Item</span>
                          </v-tooltip>
                </td>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>
    <v-dialog
          v-model="dialog"
          max-width="500"
          v-if="$auth.user().AuthScope.includes('topic:manage')"
        >
          <v-card>
            <v-card-title class="headline">Delete topic model</v-card-title>
            <v-card-text>
              Are you sure to delete this topic model?
            </v-card-text>
            <v-card-actions style="background: hsl(0, 0%, 97%)" class="pa-3">
              <v-spacer></v-spacer>
              <v-btn
                color="grey darken-1"
                flat
                depressed
                :disabled="deleteLoading"
                @click="dialog = false; itemToDelete = {};"
              >
                Cancel
              </v-btn>
              <v-btn
                color="error"
                depressed
                class="white--text"
                :loading="deleteLoading"
                :disabled="deleteLoading"
                @click.native="deleteItem()"
              >
                Delete
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
  </v-container>
</template>

<script>
export default {
  data () {
    return {
      headers: [
        {text: 'ID', sortable: true, value: 'id'},
        {text: 'Name', sortable: true, value: 'name'},
        {text: 'Language', sortable: false, value: 'language'},
        {text: 'Actions', value: 'name', sortable: false, align: 'right'}
      ],
      items: [],
      dialog: false,
      itemToDelete: {},
      deleteLoading: false,
      packageLoadingId: null,
      topicModelListLoading: false,
      pagination: {
        sortBy: 'id'
      }
    }
  },
  created () {
    this.topicModelListLoading = true
    this.fetchTopicModels()
  },
  methods: {
    fetchTopicModels () {
      this.axios.get('/topic-models')
        .then(response => {
          response.data.forEach(topicModel => {
            this.items.push({
              id: topicModel.id,
              language: topicModel.language,
              name: topicModel.name
            })
          })
        })
        .catch(error => {
          this.handleAxiosError(error)
        })
        .finally(() => {
          this.topicModelListLoading = false
        })
    },
    downloadPackage (item) {
      this.packageLoadingId = item.id
      this.axios.get(`/topic-models/${item.id}/package`, { responseType: 'blob' })
        .then(response => {
          const disposition = response.headers['content-disposition'] || ''
          const match = disposition.match(/filename=([^;]+)/)
          const fileName = match ? match[1].trim() : `topic_model_${item.id}_package.zip`
          const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }))
          const link = document.createElement('a')
          link.href = url
          link.setAttribute('download', fileName)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)
        })
        .catch(error => {
          this.handleAxiosError(error)
        })
        .finally(() => {
          this.packageLoadingId = null
        })
    },
    deleteItem () {
      this.deleteLoading = true
      this.axios.delete(`/topic-models/${this.itemToDelete.id}`)
        .then(response => {
          let index = this.items.indexOf(this.itemToDelete)
          console.log(index)
          this.items.splice(index, 1)
        })
        .catch(error => {
          this.handleAxiosError(error)
        })
        .finally(() => {
          this.deleteLoading = false
          this.dialog = false
          this.itemToDelete = {}
        })
    }
  }
}
</script>

<style>

</style>
